/**
 * @copyright
 * Copyright(c) 2020 SICK AG
 */
import { getDecimalDataSeriesCount, getEnumDataSeriesCount, getEnumDataSeriesValues, isSamplingPeriod, samplingPeriodToMillis } from './PlotterTools.js';
/**
 * Validates that the expected sampling-period fits to the given samples.
 * If not, a warning will be logged to the console.
 */

export function validateExpectedSamplingPeriod(expectedSamplingPeriod, samples) {
  const numberOfSamples = samples.length;

  if (numberOfSamples <= 1) {
    // no validation possible.
    return;
  }

  let averageSamplingPeriodMillis = 0.0;

  for (let i = 1; i < numberOfSamples; i++) {
    averageSamplingPeriodMillis += (samples[i].timestamp - samples[i - 1].timestamp) / numberOfSamples;
  }

  averageSamplingPeriodMillis = Math.round(averageSamplingPeriodMillis);
  const expectedSamplingPeriodMillis = samplingPeriodToMillis(expectedSamplingPeriod);

  if (averageSamplingPeriodMillis / 20.0 > expectedSamplingPeriodMillis || averageSamplingPeriodMillis * 20.0 < expectedSamplingPeriodMillis) {
    // eslint-disable-next-line
    console.warn(`The selected expected sampling period is "${expectedSamplingPeriod}" which equals ${expectedSamplingPeriodMillis.toLocaleString()} milliseconds between two consecutive samples.
The average sampling period of the added samples is ${averageSamplingPeriodMillis.toLocaleString()} milliseconds.
Please ADJUST THE EXPECTED SAMPLING PERIOD to better fit the added samples.`);
  }
}
/**
 * Validates that the expected sampling-period fits the given visible time-range.
 * If not, a warning will be logged to the console.
 */

export function validateVisibleTimeRange(visibleTimeRange, expectedSamplingPeriod) {
  const visibleDurationMillis = visibleTimeRange.end - visibleTimeRange.start;
  const expectedSamplingPeriodMillis = samplingPeriodToMillis(expectedSamplingPeriod);
  const expectedVisibleDurationMinMillis = expectedSamplingPeriodMillis * 30; // at least 30 samples visible

  const expectedVisibleDurationMaxMillis = expectedSamplingPeriodMillis * 2000; // at most 2000 samples visible

  if (visibleDurationMillis < expectedVisibleDurationMinMillis || visibleDurationMillis > expectedVisibleDurationMaxMillis) {
    const formattedVisibleTimeRangeStart = new Date(visibleTimeRange.start).toISOString();
    const formattedVisibleTimeRangeEnd = new Date(visibleTimeRange.end).toISOString(); // eslint-disable-next-line

    console.warn(`The visible time range is [${formattedVisibleTimeRangeStart} - ${formattedVisibleTimeRangeEnd}] which equals ${visibleDurationMillis.toLocaleString()} milliseconds.
The expected sampling period is ${expectedSamplingPeriodMillis.toLocaleString()} milliseconds.
Assuming that two samples are at least 3 pixels apart this implies that the plotter is ${(visibleDurationMillis * 3 / expectedSamplingPeriodMillis).toLocaleString()} pixels wide.
Please ADJUST THE VISIBLE TIME RANGE to better fit the expected sampling period.`);
  }
}
/**
 * Repair samples in place.
 *
 * Ensures that
 * * all samples have the same number of decimal data-series
 * * all samples have the same number of enum data-series
 * * all samples are not empty
 * * all enum values are within [0..15]; other values are mapped to Number.NaN
 * * Sample#decimalValues is used instead of the deprecated Sample#values
 */

export function sanitizeSamples(samples) {
  if (samples.length === 0) {
    return [];
  }

  samples = ensureSameDecimalSeriesCount(samples);
  samples = ensureSameEnumSeriesCount(samples);
  samples = ensureEnumValuesAreInRange(samples);
  samples = dropEmptySamples(samples);
  return ensureWellDefinedValues(samples);
}

function ensureSameDecimalSeriesCount(samples) {
  if (samples.length === 0) {
    return samples;
  }

  const latestSample = samples[samples.length - 1];
  const latestDecimalDataSeriesCount = getDecimalDataSeriesCount(latestSample);

  for (let sampleIndex = 0; sampleIndex < samples.length - 1; sampleIndex++) {
    const currentDecimalDataSeriesCount = getDecimalDataSeriesCount(samples[sampleIndex]);

    if (latestDecimalDataSeriesCount !== currentDecimalDataSeriesCount) {
      // eslint-disable-next-line no-console
      console.warn(`The latest sample contains ${latestDecimalDataSeriesCount} decimal data-series but the ${sampleIndex + 1}. sample contains ${currentDecimalDataSeriesCount} decimal data-series.\nOnly the latest sample will be used.`);
      return [latestSample];
    }
  }

  return samples;
}

function ensureSameEnumSeriesCount(samples) {
  if (samples.length === 0) {
    return samples;
  }

  const latestSample = samples[samples.length - 1];
  const latestEnumDataSeriesCount = getEnumDataSeriesCount(latestSample);

  for (let sampleIndex = 0; sampleIndex < samples.length - 1; sampleIndex++) {
    const currentEnumDataSeriesCount = getEnumDataSeriesCount(samples[sampleIndex]);

    if (latestEnumDataSeriesCount !== currentEnumDataSeriesCount) {
      // eslint-disable-next-line no-console
      console.warn(`The latest sample contains ${latestEnumDataSeriesCount} enum data-series but the ${sampleIndex + 1}. sample contains ${currentEnumDataSeriesCount} enum data-series.\nOnly the latest sample will be used.`);
      return [latestSample];
    }
  }

  return samples;
}
/**
 * Drops all empty samples.
 *
 * This function requires that
 * * the decimal data-series count is the same for every sample
 * * the enum data-series count is the same for every sample
 *
 * @param samples - the samples to be processed
 */


function dropEmptySamples(samples) {
  if (samples.length === 0) {
    return samples;
  } // Since all samples must have the same decimal data-series count
  // and enum data-series count at this point it suffices to look at
  // the first sample.


  const firstSample = samples[0];
  const decimalDataSeriesCount = getDecimalDataSeriesCount(firstSample);
  const enumDataSeriesCount = getEnumDataSeriesCount(firstSample);

  if (decimalDataSeriesCount === 0 && enumDataSeriesCount === 0) {
    // eslint-disable-next-line no-console
    console.warn('The samples do neither contain decimal values nor enum values. The samples will be dropped.');
    return [];
  }

  return samples;
}

function ensureEnumValuesAreInRange(samples) {
  if (samples.length === 0) {
    return samples;
  }

  for (let sampleIndex = 0; sampleIndex < samples.length; sampleIndex++) {
    const enumValues = getEnumDataSeriesValues(samples[sampleIndex]);

    for (let enumValueIndex = 0; enumValueIndex < enumValues.length; enumValueIndex++) {
      const enumValue = enumValues[enumValueIndex];

      if (enumValue < 0 || enumValue > 15) {
        // eslint-disable-next-line no-console
        console.warn(`The ${enumValueIndex}. enum value of the ${sampleIndex + 1}. sample is ${enumValue} which is not within [0..15].\nThe enum value will not be used.`);
        enumValues[enumValueIndex] = Number.NaN; // repair in-place
      }
    }
  }

  return samples;
}

function ensureWellDefinedValues(samples) {
  if (samples.length === 0) {
    return [];
  }

  return samples.map(sample => {
    var _ref, _sample$decimalValues, _sample$enumValues;

    return {
      timestamp: sample.timestamp,
      decimalValues: (_ref = (_sample$decimalValues = sample.decimalValues) !== null && _sample$decimalValues !== void 0 ? _sample$decimalValues : sample.values) !== null && _ref !== void 0 ? _ref : [],
      values: [],
      // deprecated
      enumValues: (_sample$enumValues = sample.enumValues) !== null && _sample$enumValues !== void 0 ? _sample$enumValues : []
    };
  });
}
/**
 * Ensures the correct type of the given sampling-period.
 */


export function sanitizeSamplingPeriod(samplingPeriod, fallback = 'EveryHundredMillis') {
  if (isSamplingPeriod(samplingPeriod)) {
    return samplingPeriod;
  }

  if (!isSamplingPeriod(fallback)) {
    throw new Error(`The provided fallback "${fallback}" is not of the correct type`);
  } // eslint-disable-next-line


  console.warn(`"${samplingPeriod}" is not a valid sampling period; "${fallback}" will be used instead.`);
  return fallback;
}
/**
 * INTERNAL USE ONLY!
 * Checks that the labels are the same if provided.
 * @param enumLegendLabels - labels defined via the enumLegendLabels-property
 * @param enumDataSeriesConfigs - contains the labels defined via the enumDataSeriesConfigs-property
 */

export function validateEnumLabels(enumLegendLabels, enumDataSeriesConfigs) {
  if (enumLegendLabels.length === 0) {
    // enumDataSeriesConfigs is the only source for labels
    return;
  }

  if (enumDataSeriesConfigs.length === 0) {
    // enumLegendLabels is the only source for labels
    return;
  }

  const labelsLength = enumLegendLabels.length;
  const configsLength = enumDataSeriesConfigs.length;

  if (labelsLength !== configsLength) {
    // eslint-disable-next-line
    console.warn(`the number of labels defined via enumLegendLabels (${labelsLength}) differs from the number of configurations defined via enumDataSeriesConfigs (${configsLength})`);
  }

  const minLength = Math.min(labelsLength, configsLength);

  for (let index = 0; index < minLength; index++) {
    const legendLabel = enumLegendLabels[index];
    const configLabel = enumDataSeriesConfigs[index].label;

    if (configLabel !== undefined && configLabel !== legendLabel) {
      // eslint-disable-next-line
      console.warn(`the label at index ${index} of enumLegendLabels ("${legendLabel}") differs from the label defined in enumDataSeriesConfigs at the same index ("${configLabel}")`);
    }
  }
}
